/** @format */

import React, { Suspense, useState } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";
import { motion } from "framer-motion";
import {
  Wrench,
  Calendar,
  Clock,
  Users,
  FileText,
  Settings,
  Search,
  Plus,
  HelpCircle,
  List,
  Eye,
} from "lucide-react";
import { Link } from "react-router-dom";

// Préchargement du modèle
useGLTF.preload("../models/2024__peugeot_9x8_lemans_spoiler.glb");

function Model() {
  const { scene } = useGLTF("/models/2014_lamborghini_veneno_roadster.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className='fixed inset-0'>
      <color attach='background' args={["#1e1e2e"]} />
      <Suspense fallback={null}>
        <Stage environment='city'>
          <Model />
        </Stage>
        <Environment preset='city' />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

const ApplicationGarage = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const features = [
    {
      icon: <Calendar className='w-6 h-6' />,
      title: "Gestion des Rendez-vous",
      description:
        "Planifiez et gérez facilement les rendez-vous clients pédagogique",
    },
    {
      icon: <Wrench className='w-6 h-6' />,
      title: "Suivi des Réparations",
      description:
        "Suivez l'état d'avancement des réparations en temps réel et la liste des apprenti(e)s ayant réalisés la réparation",
    },
    {
      icon: <Clock className='w-6 h-6' />,
      title: "Historique Maintenance",
      description: "Accédez à l'historique complet des interventions",
    },
    {
      icon: <Users className='w-6 h-6' />,
      title: "Gestion Clients",
      description: "Base de données clients centralisée et organisée",
    },
    {
      icon: <FileText className='w-6 h-6' />,
      title: "Gestion des sections",
      description: "Identifications des sections automobile et carrosserie",
    },
    {
      icon: <Settings className='w-6 h-6' />,
      title: "Suivi Pédagogique",
      description:
        "Identifier correctement les besoins pédagogiques des formateurs mécanique et carrosserie",
    },
  ];

  // Actions rapides - CHEMINS CORRIGÉS selon App.jsx ✅
  const quickActions = [
    {
      name: "Nouveau Rendez-vous",
      path: "/pole-auto/calendar",
      icon: <Plus className='w-5 h-5' />,
    },
    {
      name: "Nouvelle Intervention",
      path: "/pole-auto/interventions/form", // ✅ Route qui existe dans App.jsx
      icon: <Plus className='w-5 h-5' />,
    },
    {
      name: "Liste des Interventions",
      path: "/pole-auto/interventions-list", // ✅ Route qui existe dans App.jsx
      icon: <List className='w-5 h-5' />,
    },
    {
      name: "Nouveau Client",
      path: "/pole-auto/clients",
      icon: <Plus className='w-5 h-5' />,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className='min-h-screen py-40'>
      <div className='fixed inset-0 z-0'>
        <CarScene />
      </div>

      <Loader />

      <div className='container mx-auto px-4 relative z-10'>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className='text-center mb-20'>
          <motion.h1
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className='text-4xl md:text-6xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'>
            Application de Gestion Garage Pédagogique
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className='text-xl text-center text-white font-medium max-w-2xl mx-auto mb-12 bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm'>
            Une solution complète pour gérer efficacement votre garage
            automobile et suivre la progression pédagogique des apprentis
          </motion.p>

          <div className='relative max-w-2xl mx-auto'>
            <Search className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Rechercher...'
              className='w-full bg-slate-800/60 backdrop-blur-sm rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto'>
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className='bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 hover:bg-slate-700/60 transition-colors cursor-pointer border-l-4 border-blue-500 hover:border-pink-500'>
              <div className='flex items-start space-x-4'>
                <motion.div
                  className='p-3 bg-slate-700/50 rounded-lg'
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}>
                  <div className='text-blue-400'>{feature.icon}</div>
                </motion.div>
                <div className='flex-grow'>
                  <h3 className='text-xl font-semibold text-white mb-2'>
                    {feature.title}
                  </h3>
                  <p className='text-gray-300 mb-4'>{feature.description}</p>
                  <div className='space-y-2'>
                    <motion.div
                      className='h-1.5 bg-blue-600/50 rounded-full'
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "reverse",
                        delay: index * 0.2,
                      }}
                    />
                    <motion.div
                      className='h-1.5 bg-blue-400/50 rounded-full'
                      initial={{ width: "0%" }}
                      animate={{ width: "75%" }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        repeatType: "reverse",
                        delay: index * 0.3,
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className='bg-slate-800/60 backdrop-blur-sm rounded-lg p-8 mb-12 max-w-4xl mx-auto'>
          <div className='flex items-center space-x-3 mb-6'>
            <motion.div
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "linear",
              }}>
              <Settings className='w-6 h-6 text-blue-400' />
            </motion.div>
            <h2 className='text-2xl font-bold text-white'>Actions Rapides</h2>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {quickActions.map((action, index) => (
              <Link to={action.path} key={index} className='block'>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className='group relative flex flex-col items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white py-6 px-4 rounded-lg transition-all overflow-hidden min-h-[120px]'>
                  <motion.div
                    className='absolute inset-0 bg-blue-400/20'
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                      delay: index * 0.2,
                      ease: "linear",
                    }}
                  />
                  <div className='relative z-10 flex flex-col items-center gap-2'>
                    {action.icon}
                    <span className='font-semibold text-center text-sm'>
                      {action.name}
                    </span>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className='flex justify-center mb-20'>
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='px-8 py-4 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2'>
              <HelpCircle className="w-5 h-5" />
              Aide & Support
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ApplicationGarage;